import React from "react";

const Tooltip = ({ content, children, placement = "bottom" }) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [shouldShow, setShouldShow] = React.useState(false);
    const wrapperRef = React.useRef(null);
    const childRef = React.useRef(null);

    // Detect if content is clamped
    React.useEffect(() => {
        const el = childRef.current;
        if (!el) return;

        const isClamped =
            el.scrollHeight > el.clientHeight + 1 ||
            el.scrollWidth > el.clientWidth + 1;

        setShouldShow(isClamped);
    }, [content]);

    // Hide tooltip when touching outside (for mobile)
    React.useEffect(() => {
        const handleTouchOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsVisible(false);
            }
        };
        if (isVisible) {
            document.addEventListener("touchstart", handleTouchOutside);
        } else {
            document.removeEventListener("touchstart", handleTouchOutside);
        }
        return () => document.removeEventListener("touchstart", handleTouchOutside);
    }, [isVisible]);

    const toggleTooltip = (e) => {
        e.stopPropagation();
        if (shouldShow) setIsVisible((prev) => !prev);
    };

    const baseStyle = {
        position: "absolute",
        zIndex: 9999,
        backgroundColor: "#1f2937",
        color: "#fff",
        fontSize: "13px",
        padding: "6px 10px",
        borderRadius: "6px",
        whiteSpace: "pre-line",
        width: "max-content",
        maxWidth: "300px",
        wordWrap: "break-word",
        pointerEvents: "none",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.2s ease-in-out",
        boxSizing: "border-box",
    };

    const placementStyles = {
        top: {
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginBottom: "4px",
        },
        bottom: {
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: "4px",
        },
    };

    const arrowStyle = {
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        width: 0,
        height: 0,
        borderLeft: "6px solid transparent",
        borderRight: "6px solid transparent",
        ...(placement === "bottom"
            ? {
                  top: "-6px",
                  borderBottom: "6px solid #1f2937",
              }
            : {
                  bottom: "-6px",
                  borderTop: "6px solid #1f2937",
              }),
    };

    return (
        <div
            ref={wrapperRef}
            style={{ position: "relative", display: "inline-block" }}
            onMouseEnter={() => shouldShow && setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
            onTouchStart={toggleTooltip}
        >
            {React.cloneElement(children, { ref: childRef })}
            {shouldShow && isVisible && (
                <div style={{ ...baseStyle, ...placementStyles[placement] }}>
                    {content}
                    <div style={arrowStyle} />
                </div>
            )}
        </div>
    );
};

export default Tooltip;